import { Person } from '@mui/icons-material';
import { useMediaQuery } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import moment from 'moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { SolaForceDataGrid } from '~/components/SolaForceDataGrid';
import {
  AnalyticsReportEmployeeScope,
  AnalyticsReportProps,
  AnalyticsReportRoleScope,
  AnalyticsReportScope,
  AnalyticsReportTypes,
  WorkExperienceOptionalSettings,
  WorkExperienceReportCustomParams,
  useWorkExperienceReportData,
} from '~/data/reports';
import {
  ReportColumnsVisibility,
  ReportDataExport,
  ReportDatasetLayout,
  ReportDatasetRowCount,
  ReportDatasetSelector,
  ReportDatasetSelectorParamsLayout,
  ReportDateRangeSelector,
  ReportDateRangeSelectorMode,
  ReportEmployeeScopeSelector,
  ReportEmployeeScopeValue,
  ReportViewLayout,
  useReportDataset,
} from '~/scenes/PortalModules/Analytics/common';
import { useWorkExperienceReport } from '~/scenes/PortalModules/Analytics/People/WorkExperienceReport/useWorkExperienceReport';
import {
  WorkExperienceReportCustomParamsSelection,
  WorkExperienceReportCustomParamsSelector,
} from '~/scenes/PortalModules/Analytics/People/WorkExperienceReport/WorkExperienceReportCustomParamsSelector';

export const WorkExperienceReport = () => {
  const { t } = useTranslation();

  const isSmallScreen = useMediaQuery('(max-height:650px)');

  const loggedInEmployeeId = useLoggedInEmployeeId();
  const apiRef = useGridApiRef();

  const defaultReportSelection: AnalyticsReportProps<WorkExperienceReportCustomParams> =
    {
      reportType: {
        type: AnalyticsReportTypes.Team,
        role: AnalyticsReportRoleScope.Manager,
        employeeId: loggedInEmployeeId,
        scope: AnalyticsReportScope.Direct,
      },
      params: {
        startDate: moment().toDate(),
        endDate: moment().toDate(),
        empScope: AnalyticsReportEmployeeScope.CurrentEmployees,
        ...Object.fromEntries(
          Object.values(WorkExperienceOptionalSettings).map((key) => [
            key,
            false,
          ]),
        ),
      },
    };

  const reportDatasetProps = useReportDataset({
    selection: defaultReportSelection,
    config: {
      options: {
        types: [
          AnalyticsReportTypes.Team,
          AnalyticsReportTypes.HR,
          AnalyticsReportTypes.Unit,
        ],
        employeeScopes: [
          AnalyticsReportEmployeeScope.CurrentEmployees,
          AnalyticsReportEmployeeScope.AllEmployees,
          AnalyticsReportEmployeeScope.ActiveEmployees,
          AnalyticsReportEmployeeScope.EndedEmployees,
        ],
      },
    },
  });

  const requestProps = useMemo(() => {
    const reportSelection = reportDatasetProps.reportSelection;

    if (!!reportSelection.params) {
      const {
        startDate, // eslint-disable-line @typescript-eslint/no-unused-vars
        ...params
      } = reportSelection.params;

      return {
        ...reportSelection,
        params,
      };
    }

    return reportSelection;
  }, [reportDatasetProps.reportSelection]);

  const {
    data,
    isLoading: isLoadingReportData,
    failedToLoad,
  } = useWorkExperienceReportData(requestProps);

  const { table } = useWorkExperienceReport({ data, requestProps });

  return (
    <ReportViewLayout
      heading={t('laWorkExperienceReport')}
      slots={{
        toolbar: {
          main: (
            <ReportDatasetLayout showHeading={true}>
              <ReportDatasetSelector
                {...reportDatasetProps}
                isLoading={isLoadingReportData}
                failedToLoad={failedToLoad}
                renderCurrentParams={({ params }) => {
                  const enabledCustomParams = Object.values(
                    WorkExperienceOptionalSettings,
                  ).filter((key) => (!!params ? params[key] : false));

                  return (
                    <>
                      {moment(params?.startDate).format('L')}{' '}
                      <ReportEmployeeScopeValue empScope={params?.empScope} />
                      <WorkExperienceReportCustomParamsSelection
                        enabledCustomParams={enabledCustomParams}
                      />
                    </>
                  );
                }}
                renderParamsFields={({ params, onChange, reportConfig }) => {
                  return (
                    <ReportDatasetSelectorParamsLayout>
                      <ReportDateRangeSelector
                        startDate={params?.startDate}
                        endDate={params?.endDate}
                        onRangeChange={(startDate, endDate) =>
                          onChange({ ...params, startDate, endDate })
                        }
                        minDate={moment()
                          .startOf('year')
                          .subtract(9, 'years')
                          .toDate()}
                        maxDate={moment().toDate()}
                        mode={ReportDateRangeSelectorMode.SingleDateRange}
                      />
                      <ReportEmployeeScopeSelector
                        reportConfig={reportConfig}
                        employeeScope={params?.empScope}
                        onChange={(empScope) => {
                          onChange({ ...params, empScope });
                        }}
                      />
                      <WorkExperienceReportCustomParamsSelector
                        params={params}
                        onChange={onChange}
                      />
                    </ReportDatasetSelectorParamsLayout>
                  );
                }}
              />
              <ReportDatasetRowCount
                count={table.rows.length}
                description={t('laTotalRecords')}
                icon={<Person />}
              />
            </ReportDatasetLayout>
          ),
          actions: (
            <>
              <ReportColumnsVisibility
                apiRef={apiRef}
                columnGroupingModel={table.columnGroupingModel}
                isLoading={isLoadingReportData}
              />
              <ReportDataExport
                title={t('laCustomPersonelList')}
                startDate={reportDatasetProps.reportSelection.params?.startDate}
                apiRef={apiRef}
                isLoading={isLoadingReportData}
              />
            </>
          ),
        },
      }}
      slotProps={{
        content: {
          sx: {
            minHeight: isSmallScreen ? 'calc(100vh - 100px)' : 0,
          },
        },
      }}
    >
      <SolaForceDataGrid
        apiRef={apiRef}
        columns={table.columnConfigs}
        rows={table.rows}
        columnGroupingModel={table.columnGroupingModel}
        getRowId={(row) => row.employeeId}
        pinnedColumns={{
          left: ['employeeId', 'firstName', 'lastName'],
        }}
        columnHeaderHeight={36}
      />
    </ReportViewLayout>
  );
};
